export function htmlHasBlankChips(html: string): boolean {
  return html.includes('data-blank-id');
}

/** Every blank id in the question HTML, in document order — the single source of truth for
 * "Answer N" numbering (derived fresh, never stored, so it can't go stale across add/remove/
 * reorder) and for reconciling a block's `blanks[]` config array against the live passage. */
export function getBlankIdsInOrder(html: string): string[] {
  if (!html || !htmlHasBlankChips(html)) return [];
  const div = document.createElement('div');
  div.innerHTML = html;
  return [...div.querySelectorAll<HTMLElement>('[data-blank-id]')]
    .map(el => el.getAttribute('data-blank-id') ?? '')
    .filter(Boolean);
}

// A filled/placeholder blank renders as an inert "textbox" (matches FitbInteractiveQuestion's own
// real <input> and the app-wide 'rounded border border-ws-border bg-ws-btn ...' input convention)
// rather than a colored pill — the pill recipe previously used here was visually indistinguishable
// from a variable chip (globals.css's .ws-var-chip) except for hue, which read
// as "this blank is a variable" to a teacher scanning the passage. Plain HTML string manipulation,
// not JSX, so reimplemented as inline CSS against the same tokens.
function textboxDisplayStyle(opts: { borderColorVar?: string; textColorVar: string; dashed?: boolean; italic?: boolean }): string {
  return [
    'display:inline-flex',
    'align-items:center',
    'border-radius:4px',
    `border:1px ${opts.dashed ? 'dashed' : 'solid'} var(${opts.borderColorVar ?? '--color-ws-border'})`,
    'background:var(--color-ws-btn)',
    'padding:2px 6px',
    'font-family:var(--font-mono, monospace)',
    'font-size:0.875rem',
    opts.italic ? 'font-style:italic' : '',
    `color:var(${opts.textColorVar})`,
    'vertical-align:middle',
    'position:relative',
    'top:-1px',
    'white-space:nowrap',
  ].filter(Boolean).join(';');
}

const BLANK_TEXTBOX_STYLE = textboxDisplayStyle({ textColorVar: '--color-ws-text' });
// The isPreview "Answer N" label is a placeholder, not a real value — dashed border + muted
// italic text reads as "nothing typed here yet", distinct from a filled textbox.
const BLANK_TEXTBOX_PLACEHOLDER_STYLE = textboxDisplayStyle({ textColorVar: '--color-ws-muted', dashed: true, italic: true });
// A blank still missing a real correct answer keeps the same textbox shape but in warning color —
// the same signal as the collapsed-card's "No Correct Answers"/"Missing Correct Answers" bar, but
// localized to exactly which blank needs attention.
const BLANK_TEXTBOX_WARNING_STYLE = textboxDisplayStyle({ borderColorVar: '--color-ws-warning-fg', textColorVar: '--color-ws-warning-fg', dashed: true, italic: true });

/** Renders blank-chip spans as styled "Answer N" textboxes — used for isPreview only. A student
 * shouldn't see the correct answer in the simulated-student preview, so this stays generic,
 * unlike the collapsed-card teacher-facing display (see renderBlanksWithAnswers below). */
export function renderBlanksForDisplay(html: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!html || !htmlHasBlankChips(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  const idsInOrder = getBlankIdsInOrder(html);
  div.querySelectorAll<HTMLElement>('[data-blank-id]').forEach(span => {
    const id = span.getAttribute('data-blank-id') ?? '';
    const ordinal = idsInOrder.indexOf(id) + 1;
    const chip = document.createElement('span');
    chip.setAttribute('data-blank-id-display', id);
    chip.setAttribute('style', BLANK_TEXTBOX_PLACEHOLDER_STYLE);
    chip.textContent = t('blocks.fitb.blankChipLabel', { ordinal });
    span.replaceWith(chip);
  });
  return div.innerHTML;
}

interface BlankLike {
  id: string;
  words: { text: string; role: 'correct' | 'neutral' | 'incorrect' }[];
}

/** Renders blank-chip spans as textboxes showing that blank's own accepted correct word(s) — used
 * for the teacher-facing collapsed-card display (mirrors Keyword's collapsed card showing the
 * actual accepted keywords directly). Falls back to the generic "Answer N" label if a blank has no
 * correct word configured yet. */
export function renderBlanksWithAnswers(html: string, blanks: BlankLike[], t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!html || !htmlHasBlankChips(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  const idsInOrder = getBlankIdsInOrder(html);
  div.querySelectorAll<HTMLElement>('[data-blank-id]').forEach(span => {
    const id = span.getAttribute('data-blank-id') ?? '';
    const ordinal = idsInOrder.indexOf(id) + 1;
    const blank = blanks.find(b => b.id === id);
    const correctWords = (blank?.words ?? []).filter(w => w.role === 'correct').map(w => w.text.trim()).filter(Boolean);
    const chip = document.createElement('span');
    chip.setAttribute('data-blank-id-display', id);
    chip.setAttribute('style', correctWords.length > 0 ? BLANK_TEXTBOX_STYLE : BLANK_TEXTBOX_WARNING_STYLE);
    chip.textContent = correctWords.length > 0 ? correctWords.join(', ') : t('blocks.fitb.blankChipLabel', { ordinal });
    span.replaceWith(chip);
  });
  return div.innerHTML;
}
